import { Tooltip } from 'antd'
import { useFormik } from 'formik'
import { useSearchParams } from 'react-router-dom'
import { FC } from 'react'
import * as Yup from 'yup'
import Button from '~/app/components/utils/Button/Button'
import Icon from '~/app/components/utils/Icon'
import Input from '~/app/components/utils/Input/Input'
import Text from '~/app/components/utils/Text/Text'
import { ResetPasswordPayload } from '~/app/services/auth'
import { useResetPassword } from '~/app/services/auth/auth.hook'

interface NewPasswordFormProps {
  onSubmit: () => void
}

const NewPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Password must be greater or equal than 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .required('Password is required'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
})

const NewPasswordForm: FC<NewPasswordFormProps> = ({ onSubmit }) => {
  const [searchParams] = useSearchParams()
  const resetToken = searchParams.get('token')
  const mutation = useResetPassword()
  const formik = useFormik<{ password: string; confirm_password: string }>({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
    validationSchema: NewPasswordSchema,
    initialValues: {
      password: '',
      confirm_password: '',
    },

    async onSubmit(values, formikHelpers) {
      if (!resetToken) {
        return
      }
      const submitData: ResetPasswordPayload = {
        new_password: values.password.trim(),
        token: resetToken?.trim(),
      }
      mutation.mutate(submitData, {
        onSuccess() {
          formikHelpers.resetForm()
          onSubmit()
        },
      })
    },
  })
  return (
    <div className="gap-7.5 flex flex-col">
      <Text type="headline-4" color="800" className="w-full text-center">
        Reset password
      </Text>
      <Text type="body-1" color="600" className="w-full text-center">
        Set the new password for your account
      </Text>
      <Input
        label="Password"
        required
        type="password"
        labelExtra={
          <Tooltip
            color="white"
            rootClassName="!max-w-[210px]"
            title={
              <Text color="600" type="body-3" align="left">
                Password must contain at least 8 characters, one uppercase letter, and one number
              </Text>
            }
          >
            <Icon name="info-circle" size={20} color="var(--grey-300)" />
          </Tooltip>
        }
        labelClassName="!justify-start"
        placeholder="Enter your password"
        {...formik.getFieldProps('password')}
        error={formik.touched.password ? formik.errors.password : undefined}
        onEnter={formik.submitForm}
      />
      <Input
        label="Confirm password"
        required
        type="password"
        placeholder="Enter your confirm password"
        {...formik.getFieldProps('confirm_password')}
        error={formik.touched.confirm_password ? formik.errors.confirm_password : undefined}
        onEnter={formik.submitForm}
      />
      <Button onClick={formik.submitForm} loading={mutation.isPending}>
        Reset password
      </Button>
    </div>
  )
}

export default NewPasswordForm
